export const siteUrl = 'https://uig-ai.online'

export const legalLinks = {
  terms: '/terms',
  privacy: '/privacy',
  mobilePrivacy: '/mobile-privacy',
  acceptableUse: '/acceptable-use',
} as const

export const legalUpdated = '26 October 2026'

export interface LegalLink {
  label: string
  href: string
}

export type LegalBlock =
  | string
  | { parts: (string | LegalLink)[] }
  | { items: string[] }

interface LegalSection {
  id: string
  title: string
  blocks: LegalBlock[]
}

export interface LegalDocument {
  title: string
  updated: string
  introduction: LegalBlock[]
  sections: LegalSection[]
}

const webSupport = { label: 'support@uig-ai.online', href: 'mailto:support@uig-ai.online' }
const mobileSupport = { label: 'jeffhick0715@outlook.com', href: 'mailto:jeffhick0715@outlook.com' }

export const mobilePrivacyDocument: LegalDocument = {
  title: 'Mobile Privacy Policy',
  updated: legalUpdated,
  introduction: [
    'This Privacy Policy explains how UIG collects, uses and protects information when you use our mobile application (the “App”). It also explains your privacy rights and choices.',
  ],
  sections: [
    {
      id: 'information-we-collect',
      title: 'Information We Collect',
      blocks: [
        'When you use UIG, we may process:',
        {
          items: [
            'Usage Data: device information, app activity and diagnostic data needed to operate and improve the App.',
            'User Content: photos, videos, text prompts and settings you choose for AI generation.',
          ],
        },
        'We process this information to provide the App and its features, resolve technical issues and improve the Service.',
      ],
    },
    {
      id: 'ai-content-generation',
      title: 'AI Content Generation',
      blocks: [
        'To create images or videos, the App sends the prompt, selected media and generation settings to a third-party AI processing service. The service processes this information to complete your request.',
        'When you submit photos or videos for AI generation, these materials are sent to a third-party AI service solely to create the content you request.',
      ],
    },
    {
      id: 'photos-and-face-data',
      title: 'Photos and Face Data',
      blocks: [
        'You may choose photos containing faces for AI generation. UIG does not use these photos to identify people, perform facial recognition or create biometric profiles.',
        'Photos are submitted for the generation you request, not for advertising by UIG. The external AI service processes the submitted images under its applicable privacy and retention terms.',
      ],
    },
    {
      id: 'storage-and-retention',
      title: 'Storage and Retention',
      blocks: [
        'Your photos, videos, and generated content are stored locally on your device. You can delete content stored within the App by uninstalling the App from your device.',
        'Photos and videos saved separately to your device’s photo library can be deleted directly from your photo library.',
        'Content processed by third-party AI services may be retained according to their own data retention policies.',
      ],
    },
    {
      id: 'third-party-services',
      title: 'Third-Party Services',
      blocks: [
        'We use third-party services to process AI generation requests. We may also use services that support app operations, purchases and diagnostics. Each service processes only the information relevant to its function, under applicable agreements and policies.',
        'Data may be processed in countries other than your own. Where required by law, appropriate safeguards apply.',
      ],
    },
    {
      id: 'security',
      title: 'Security',
      blocks: [
        'We take reasonable steps to protect the information processed through UIG. However, no method of online transmission or electronic storage is completely secure.',
      ],
    },
    {
      id: 'your-choices-and-rights',
      title: 'Your Choices and Rights',
      blocks: [
        'If you have any questions about how your information or content is processed or stored, you can contact us using the details provided in this Privacy Policy.',
      ],
    },
    {
      id: 'childrens-privacy',
      title: 'Children’s Privacy',
      blocks: [
        'UIG is intended for users aged 18 and older. Our Service is not intended for minors, and we do not knowingly collect personal information from anyone under the age of 18.',
      ],
    },
    {
      id: 'ai-acceptable-use-policy',
      title: 'AI Acceptable Use Policy',
      blocks: [
        {
          parts: [
            'We encourage responsible use of our AI generation features. Please review our ',
            { label: 'AI Acceptable Use Policy', href: legalLinks.acceptableUse },
            ' for information about prohibited content and responsible use of UIG.',
          ],
        },
      ],
    },
    {
      id: 'changes-and-contact',
      title: 'Changes and Contact',
      blocks: [
        'We may update this Privacy Policy from time to time. The updated version will be published with a new “Last updated” date.',
        { parts: ['For questions about this Privacy Policy, contact us at: ', mobileSupport] },
      ],
    },
  ],
}

export const privacyDocument: LegalDocument = {
  title: 'Web Privacy Policy',
  updated: legalUpdated,
  introduction: [],
  sections: [
    {
      id: 'about-this-policy',
      title: 'About This Policy',
      blocks: [
        'This Privacy Policy explains how UIG collects, uses, stores, and shares information when you use our website.',
      ],
    },
    {
      id: 'information-we-collect',
      title: 'Information We Collect',
      blocks: [
        'When you use UIG, we may collect and process the following information:',
        {
          items: [
            'Email address, account identifier, and profile information provided when signing in.',
            'Photos, videos, text prompts, and generation settings.',
            'Generated content, creation history, and generation details.',
            'Credit balances, subscription status, and purchase records.',
            'Messages and contact information provided when contacting support.',
            'IP address, browser information, session details, and security logs.',
          ],
        },
      ],
    },
    {
      id: 'how-we-use-information',
      title: 'How We Use Information',
      blocks: [
        'We use your information to provide access to your account, generate images and videos based on your requests, save your results and generation history, manage your credits and purchases, respond to support requests, keep the website secure, and resolve technical issues.',
      ],
    },
    {
      id: 'ai-generation-and-media',
      title: 'AI Generation and Media',
      blocks: [
        "When you request AI generation, the relevant prompts, photos, videos and settings are sent to external AI processing services to produce the requested result. We do not use private uploads or generated results for UIG's own advertising or AI model training.",
        'External AI services may temporarily store inputs and outputs under their own retention practices. Storage periods can vary between providers and may change. We do not promise a fixed deletion period for material processed by external providers.',
      ],
    },
    {
      id: 'sharing-information',
      title: 'Sharing Information',
      blocks: [
        'We share information with service providers that support the operation of our website, including sign-in, hosting and storage, AI generation, payments, support, and security. We share only the information needed for their respective functions.',
        'We may also disclose information when required by law or a valid request from an authorized authority, or in connection with a sale or merger of our business.',
      ],
    },
    {
      id: 'payments',
      title: 'Payments',
      blocks: [
        'Payments are handled by third-party payment services. UIG receives purchase identifiers, transaction and subscription status, and credit information needed to provide paid features. Card details entered on a hosted checkout page are handled by the payment processor rather than stored in UIG’s application records.',
      ],
    },
    {
      id: 'storage-and-deletion',
      title: 'Storage and Deletion',
      blocks: [
        "Your uploaded media, generated results and generation history are stored in your web account so you can access them later. Your generated content is not automatically published for other users or the public to view. You may delete your account through the Profile section; account deletion removes the related content and history from the Service's active storage.",
        { parts: ["Copies may remain temporarily in backups, technical records or external service providers' systems in accordance with their retention practices. We cannot guarantee immediate deletion from those independent systems. For questions about your data, contact ", webSupport, '.'] },
      ],
    },
    {
      id: 'cookies-and-security',
      title: 'Cookies and Security',
      blocks: [
        'We use information about your account sign-in to keep you signed in and help keep the website secure. We use reasonable measures to protect your information, but no internet service can guarantee complete security.',
      ],
    },
    {
      id: 'your-choices-and-requests',
      title: 'Your Choices and Requests',
      blocks: [
        { parts: ['You choose what media to upload and can use the available account settings. If you have questions about access to, processing or deletion of your information, contact ', webSupport, '. We will review your request. Data may be processed in countries other than your own.'] },
      ],
    },
    {
      id: 'childrens-privacy',
      title: 'Children’s Privacy',
      blocks: [
        'UIG is intended for users aged 18 and older. The service is not intended for minors, and we do not knowingly collect personal information from anyone under 18.',
      ],
    },
    {
      id: 'updates-and-contact',
      title: 'Updates and Contact',
      blocks: [
        { parts: ['We may update this Privacy Policy and publish the revised version here. Third-party websites linked from UIG have their own policies. Contact: ', webSupport, '.'] },
      ],
    },
  ],
}

export const acceptableUseDocument: LegalDocument = {
  title: 'AI Acceptable Use Policy',
  updated: legalUpdated,
  introduction: [],
  sections: [
    {
      id: 'purpose',
      title: 'Purpose',
      blocks: [
        "These rules apply to AI image and video generation in UIG on the web and in our mobile apps. Please use UIG responsibly and respect other people's rights.",
      ],
    },
    {
      id: 'photos-of-real-people',
      title: 'Photos of Real People',
      blocks: [
        "You may use your own photos or photos of other people when you have the necessary rights or permission. Do not use someone's image to violate their privacy, mislead others or cause harm.",
      ],
    },
    {
      id: 'prohibited-content',
      title: 'Prohibited Content',
      blocks: [
        'You must not use UIG to create or request:',
        {
          items: [
            'Pornographic or sexually explicit content.',
            'Any sexual content involving minors, including AI-generated content.',
            'Intimate images of real people without their consent, including AI-generated undressing.',
            'Deepfakes intended to impersonate, deceive, harass or harm others.',
            'Content promoting violence, threats, abuse or hatred.',
            'Content used for fraud or other illegal activities.',
            "Content that violates another person's privacy, copyright or other rights.",
          ],
        },
      ],
    },
    {
      id: 'your-responsibility',
      title: 'Your Responsibility',
      blocks: [
        "You are responsible for the photos, videos and prompts you submit and for how you use generated results. Make sure you have permission to use other people's materials. AI-generated content may be inaccurate and must not be presented as genuine in a way that deceives or harms others.",
      ],
    },
    {
      id: 'violations-and-reports',
      title: 'Violations and Reports',
      blocks: [
        { parts: ['We may reject generation requests or limit access to UIG when these rules are violated, where such measures are available. To report inappropriate content or ask a question about these rules, contact us at ', webSupport, ' (web) or ', mobileSupport, ' (mobile).'] },
      ],
    },
    {
      id: 'how-these-rules-apply',
      title: 'How These Rules Apply',
      blocks: [
        'These rules cover the UIG web service and mobile apps. Please review them alongside the terms that apply to the version of UIG you use. Our Privacy Policies separately explain how your information and media are processed.',
      ],
    },
    {
      id: 'changes-to-these-rules',
      title: 'Changes to These Rules',
      blocks: [
        'We may update these rules from time to time. The latest version will be available on our website.',
      ],
    },
  ],
}

export const termsDocument: LegalDocument = {
  title: 'Web Terms of Service',
  updated: legalUpdated,
  introduction: [],
  sections: [
    {
      id: 'agreement-and-eligibility',
      title: 'Agreement and Eligibility',
      blocks: [
        'These Terms govern use of the UIG web service for AI image and video generation. By accessing or using the service, you accept these Terms. UIG is intended for users aged 18 and older.',
      ],
    },
    {
      id: 'how-the-service-works',
      title: 'How the Service Works',
      blocks: [
        'You can create AI-generated images and videos using prompts, uploaded media and generation settings. Features, generation time, available models and credit costs may change. AI results can be inaccurate, unexpected or similar to results created for others. A particular result is not guaranteed.',
      ],
    },
    {
      id: 'accounts-and-security',
      title: 'Accounts and Security',
      blocks: [
        { parts: ['You are responsible for keeping access to your account and sign-in provider secure. If you notice unauthorized activity, contact ', webSupport, '.'] },
      ],
    },
    {
      id: 'credits-and-paid-features',
      title: 'Credits and Paid Features',
      blocks: [
        'All generations require credits. Credit costs and your available balance are displayed in the Service.',
        'Weekly subscriptions: included credits are valid during the corresponding weekly billing period. Unused credits expire at the end of that period.',
        'Annual subscriptions: credits are allocated monthly and unused subscription credits accumulate during the paid annual period. Any remaining annual subscription credits expire when that paid annual period ends; they do not carry over into the next annual period, including upon renewal. Turning off automatic renewal does not shorten the paid period.',
        'One-time credit purchases: separately purchased credits do not expire. They are tracked separately from subscription credits and are not affected by subscription-credit expiry.',
        'Failed generations: credits charged for a failed generation are returned to the balance from which they were deducted, retaining their original expiry terms.',
        'The number of credits and costs of generation are shown in the Service or before purchase.',
      ],
    },
    {
      id: 'subscriptions-cancellation-and-refunds',
      title: 'Subscriptions, Cancellation and Refunds',
      blocks: [
        'Subscription prices, billing periods, renewal terms and included credit allocations are shown before purchase. A subscription may renew automatically as disclosed at checkout. You can cancel future renewals through the available subscription management method.',
        { parts: ['Deleting your account does not necessarily cancel an active subscription. For questions about payment refunds, contact us at ', webSupport, '. We will review your request based on the circumstances of your purchase and applicable legal requirements.'] },
      ],
    },
    {
      id: 'your-content-and-ai-results',
      title: 'Your Content and AI Results',
      blocks: [
        'You keep the rights you own in uploaded material. You permit UIG and its necessary service providers to process, store and transform your inputs to fulfill requests, deliver results, preserve account history and protect the service. You must have permission to use any third-party materials or likenesses. UIG does not claim ownership of your private uploads. AI-generated results may not qualify for exclusive copyright protection and may affect others’ rights.',
      ],
    },
    {
      id: 'prohibited-use',
      title: 'Prohibited Use',
      blocks: [
        {
          parts: [
            'Do not create unlawful, sexually explicit, exploitative or abusive content, including sexual material involving minors, intimate images without consent, harmful impersonations and content infringing others’ rights. Additional rules are available in the UIG ',
            { label: 'AI Acceptable Use Policy', href: legalLinks.acceptableUse },
            '.',
          ],
        },
      ],
    },
    {
      id: 'limits-and-enforcement',
      title: 'Limits and Enforcement',
      blocks: [
        'We may reject generation requests, restrict usage or suspend access for abuse, security risks or violations of these Terms where such controls are available. We may update or discontinue features and perform maintenance.',
      ],
    },
    {
      id: 'privacy-and-deleting-an-account',
      title: 'Privacy and Deleting an Account',
      blocks: [
        {
          parts: [
            'Our ',
            { label: 'Web Privacy Policy', href: legalLinks.privacy },
            ' describes the handling and deletion of account information and content. You can request account deletion in Profile. Account deletion and subscription cancellation are separate actions.',
          ],
        },
      ],
    },
    {
      id: 'service-availability-and-responsibility',
      title: 'Service Availability and Responsibility',
      blocks: [
        'We aim to operate a reliable service but do not guarantee uninterrupted access or error-free AI results. Users are responsible for how they use generated content. These Terms do not remove protections that cannot be waived under applicable consumer law.',
      ],
    },
    {
      id: 'changes-and-contact',
      title: 'Changes and Contact',
      blocks: [
        { parts: ['We may update these Terms by publishing a revised version. Questions about these Terms or purchases: ', webSupport, '.'] },
      ],
    },
  ],
}

export const legalDocuments = {
  [legalLinks.terms]: termsDocument,
  [legalLinks.privacy]: privacyDocument,
  [legalLinks.mobilePrivacy]: mobilePrivacyDocument,
  [legalLinks.acceptableUse]: acceptableUseDocument,
} as const satisfies Record<string, LegalDocument>
